































































































import { computed, defineComponent, inject, ref, Ref } from "@vue/composition-api";
import draggable from "vuedraggable";
import { MetadataField, StereoFormat } from "@scrile/api-provider/dist/api/FileProvider";
import { Subject } from "@scrile/api-provider/dist/api/SubjectProvider";
import { VaultFileUsageSubjectTypeEnum } from "@scrile/api-provider/dist/api/VaultsProvider";
import useVaultView from "@/hooks/useVaultView";
import { getPostAllowedFileTypes, isAttachmentFile } from "@/lib/fileHelpers";
import config from "@/config";
import { AttachmentFile, PostFileData } from "@/types";
import BaseButton from "@/components/BaseButton";
import BaseIcon from "@/components/BaseIcon";
import BaseInputFile from "@/components/BaseInputFile";
import BaseInputFileWithPreview from "@/components/BaseInputFileWithPreview";
import BaseSpinner from "@/components/BaseSpinner";
import PostFileList from "@/components/PostFileList";
import VaultModal from "@/components/VaultModal";
import { AddingFilesOptions, DraggableEmitOptions } from "../../types";
import EditPostDropZone from "../EditPostDropZone";
import PostFileItemPreview from "../PostFileItemPreview";
import PreviewSetButton from "../PreviewSetButton";
import SelectVideoFormatModal from "../SelectVideoFormatModal";

export default defineComponent({
  name: "PostFilesArea",
  components: {
    BaseSpinner,
    BaseInputFileWithPreview,
    BaseIcon,
    BaseInputFile,
    VaultModal,
    BaseButton,
    PostFileItemPreview,
    EditPostDropZone,
    PostFileList,
    PreviewSetButton,
    SelectVideoFormatModal,
    draggable,
  },
  props: {
    fileList: { type: Array as () => PostFileData[], required: true },
    showPublicButton: { type: Boolean, default: false },
    newFilePublicAccess: { type: Boolean, default: false },
  },
  setup() {
    const loadingUploadUrls = inject<Ref<boolean>>("loadingUploadUrls", ref(false));
    const { isVaultAvailable, showPrivateVault: showVault, showVaultOrNative } = useVaultView();
    const postSubject = computed<Subject<VaultFileUsageSubjectTypeEnum.USER_CONTENT_SETS>>(() => {
      return {
        subjectId: "0",
        subjectType: VaultFileUsageSubjectTypeEnum.USER_CONTENT_SETS,
      };
    });

    return { isVaultAvailable, showVault, postSubject, loadingUploadUrls, showVaultOrNative };
  },
  data() {
    return {
      isDragAndMoveActive: false,
      isWaitingLoadStereoFile: false,
      stereoFileForChange: null as PostFileData | null,
      newStereoFileMetadata: { stereoFormat: StereoFormat.STEREO_180_LR } as Record<MetadataField, string>,
    };
  },
  computed: {
    useStereoFiles() {
      return !!config.delightVideoPlayerLicense;
    },
    allowedFileTypes(): string[] {
      return getPostAllowedFileTypes();
    },
    draggableOptions(): Record<string, string | boolean | number> {
      return {
        delay: 200,
        delayOnTouchOnly: true,
        supportPointer: false,
        disabled: this.fileList.length < 2,
        dragClass: "post-files-area__dragging",
        ghostClass: "post-files-area__ghost",
      };
    },
  },
  methods: {
    onUpdateDraggable({ newDraggableIndex, oldDraggableIndex }: DraggableEmitOptions): void {
      this.$emit("draggable:update", { newDraggableIndex, oldDraggableIndex });
      this.isDragAndMoveActive = false;
    },
    onAddFileButtonHandler(showNativeFileUploader: () => void, stereoFile = false) {
      this.isWaitingLoadStereoFile = stereoFile;
      this.showVaultOrNative(showNativeFileUploader, false);
    },
    onNativeAddFileHandler(files: FileList, stereoFile = false) {
      this.$emit("addFiles", files, {
        publicAccess: this.newFilePublicAccess,
        unshift: true,
        metadata: stereoFile ? this.newStereoFileMetadata : undefined,
      });
    },
    onAddFiles(files: (AttachmentFile | File)[], options: AddingFilesOptions): void {
      const data = {
        metadata: this.isWaitingLoadStereoFile ? this.newStereoFileMetadata : undefined,
        ...options,
      };
      if (files.every(isAttachmentFile)) {
        this.$emit("addFilesFromVault", files, data);
      } else {
        this.$emit("addFiles", files, data);
      }
      this.isWaitingLoadStereoFile = false;
    },
    stereoFormatChangeHandler(newType: StereoFormat) {
      if (this.stereoFileForChange) {
        this.$emit("file:change", { id: this.stereoFileForChange.id, metadata: { stereoFormat: newType } });
        this.stereoFileForChange = null;
      }
    },
    onShowVault(stereoFile = false) {
      this.showVault = true;
      this.isWaitingLoadStereoFile = stereoFile;
    },
    closeVault() {
      this.showVault = this.isWaitingLoadStereoFile = false;
    },
  },
});
